import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { SettingsMode } from './enum/SettingsMode.enum';
import { sendHttpRequest } from '../../utils/network.service';
import './ivanti-integration-card.css';
import { QCard, QCardContent, QTypography, QButton, QTextField } from '@kw/quokka-ui';
import { styled } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { KwFormErrorMessage } from '../../kw-ui-components/KwFormErrorMessage';
import { MDM_INTEGRATION_SETTINGS_ID } from '../shared/constants';

interface IvantiIntegrationCardProps {
  usernamePlaceholder: string;
  passwordPlaceholder: string;
  lastSync: string;
  isAdmin: boolean;
  mode: { [key: string]: SettingsMode };
  setMode: React.Dispatch<React.SetStateAction<{ [key: string]: SettingsMode }>>;
  mutate: () => void;
}

export const IvantiIntegrationCard = ({
  usernamePlaceholder,
  passwordPlaceholder,
  lastSync,
  isAdmin,
  mode,
  setMode,
  mutate,
}: IvantiIntegrationCardProps) => {
  const isEditMode = mode.ivanti === SettingsMode.EDIT;
  const isReadMode = mode.ivanti === SettingsMode.READ;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [IvantiFormErrors, setIvantiFormErrors] = useState({
    username: false,
    password: false,
    validated: false,
  });
  const [connectionFailed, setConnectionFailed] = useState(false);
  const lastSyncTimestamp = lastSync ? new Date(parseInt(lastSync, 10)).toLocaleString() : 'Never';

  const resetInputValues = () => {
    setUsername('');
    setPassword('');
    setConnectionFailed(false);
    setIvantiFormErrors({ username: false, password: false, validated: false });
    setMode({ ...mode, ivanti: SettingsMode.READ });
  };

  function validateIvantiSettings() {
    setIvantiFormErrors({
      username: !username.length,
      password: !password.length,
      validated: true,
    });
  }

  const syncNow = async () => {
    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/mdm-integrations/${MDM_INTEGRATION_SETTINGS_ID.IVANTI}/sync`,
      method: 'POST',
      body: {},
      successMessage: 'Ivanti sync initiated successfully',
      errorMessage: 'Error initiating Ivanti sync',
      enqueueSnackbar,
    });
  };

  const updateIvantiSettings = async () => {
    validateIvantiSettings();

    if (!username.length || !password.length) {
      return;
    }

    const existingSettings = username.length || password.length;
    let path = `${process.env.ORGANIZATION_SERVICE_URL}`;
    path += existingSettings ? `/organizations/mdm-integrations/${MDM_INTEGRATION_SETTINGS_ID.IVANTI}` : '/organizations/mdm-integrations';
    const method = existingSettings ? 'PUT' : 'POST';
    const body = existingSettings
      ? {}
      : { mdmIntegrationId: MDM_INTEGRATION_SETTINGS_ID.IVANTI, settings: { username: username, password: password } };

    if (existingSettings) {
      const settings = {};
      if (username.length) {
        Object.assign(settings, { ...settings, username });
      }
      if (password.length) {
        Object.assign(settings, { ...settings, password });
      }

      Object.assign(body, { settings });
    }

    await sendHttpRequest({
      path,
      method,
      body,
      successMessage: 'Ivanti settings updated successfully',
      errorMessage: 'Error updating Ivanti settings',
      enqueueSnackbar,
    });

    await syncNow();

    mutate();
    resetInputValues();
  };

  const flags: LaunchDarklyFlags['flags'] = useFlags();

  if (!flags?.ivantiIntegration || !verifyAuthPermission(AuthPermissions.SETTINGS_INTEGRATIONS_MDM)) return null;

  return (
    <StyledQCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold" gutterBottom>
          <img src="/logo/ivanti.png" className="ivanti-logo" />
          Ivanti Neurons for MDM
        </StyledQTypography>
        <KwFormErrorMessage error={Boolean(connectionFailed)} reason="We couldn't make a connection with the information provided" />
        <StyledQTypography variant="bodyRegular" gutterBottom>
          Configure Q-scout’s connection to the Ivanti Neurons API to import Mobile Device and App Information from your Ivanti Neurons for MDM tenant. The integration will pull data from your tenant’s Default Space.{' '}
          <a href="https://help.ivanti.com/mi/help/en_us/cld/admin/ivanti/120/all/en-us/About%20Cloud.htm" target="_blank" rel="noopener noreferrer">
            Learn more
          </a>
        </StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <StyledQTypography variant="h5Bold">Username</StyledQTypography>
            <QTextField
              placeholder={usernamePlaceholder}
              onChange={e => setUsername(e.target.value)}
              error={Boolean(IvantiFormErrors.username)}
            />
            <KwFormErrorMessage error={Boolean(IvantiFormErrors.username)} reason="Enter the Ivanti username" />

            <StyledQTypography variant="h5Bold">Password</StyledQTypography>
            <QTextField
              placeholder={passwordPlaceholder}
              onChange={e => setPassword(e.target.value)}
              error={Boolean(IvantiFormErrors.password)}
            />
            <KwFormErrorMessage error={Boolean(IvantiFormErrors.password)} reason="Enter the Ivanti password" />
          </>
        ) : (
          <>
            <StyledQTypography variant="h5Bold">Username</StyledQTypography>
            <StyledQTypography>{usernamePlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Password</StyledQTypography>
            <StyledQTypography>{passwordPlaceholder}</StyledQTypography>
          </>
        )}

        <div className="ivanti-action-wrapper">
          {isAdmin && isEditMode ? (
            <>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" onClick={updateIvantiSettings}>
                Save
              </QButton>
            </>
          ) : (
            <>
              {isAdmin && isReadMode ? (
                <>
                  <div className="ivanti-sync-action-wrapper">
                    <QButton
                      style={{ padding: 0 }}
                      variant="text"
                      onClick={() => syncNow()}
                      disabled={ !usernamePlaceholder || !passwordPlaceholder}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M14.3785 9.17012C13.9791 9.05293 13.5641 9.28243 13.4488 9.6798C12.7723 12.0389 10.6004 13.667 8.14102 13.667C6.06195 13.667 4.17852 12.4707 3.25039 10.667H6.38789C6.80195 10.667 7.13789 10.3311 7.13789 9.91699C7.13789 9.50293 6.83164 9.16699 6.41602 9.16699H1.41602C1.00195 9.16699 0.666016 9.50293 0.666016 9.91699V14.917C0.666016 15.3311 1.00195 15.667 1.41602 15.667C1.83008 15.667 2.16602 15.3311 2.16602 14.917V11.7264C3.40977 13.8045 5.67227 15.167 8.14102 15.167C11.2348 15.167 13.9973 13.0948 14.8629 10.1264C15.0066 9.70137 14.7754 9.31387 14.3785 9.17012ZM14.9191 0.666992C14.5066 0.666992 14.1691 1.00293 14.1691 1.41699V4.60762C12.9285 2.52793 10.6629 1.16699 8.16914 1.16699C5.07539 1.16699 2.3107 3.23918 1.44758 6.20762C1.33133 6.60449 1.55977 7.02012 1.95727 7.13574C2.35852 7.2539 2.77258 7.02246 2.88695 6.62605C3.56602 4.29512 5.73789 2.66699 8.16914 2.66699C10.2482 2.66699 12.1316 3.86324 13.0598 5.66699H9.91914C9.50664 5.66699 9.16914 6.00449 9.16914 6.41699C9.16914 6.82949 9.50664 7.16699 9.91914 7.16699H14.9191C15.3332 7.16699 15.6691 6.83105 15.6691 6.41699V1.41699C15.6691 1.00293 15.3348 0.666992 14.9191 0.666992Z"
                          fill="#244C5A"
                        />
                      </svg>
                      <span className="ivanti-sync-now-button-text">Sync Now</span>
                    </QButton>
                    <QTypography sx={{ ml: 1, color: '#757575' }}>Last synced: {lastSyncTimestamp}</QTypography>
                  </div>
                  <QButton variant="filled" onClick={() => setMode({ ...mode, ivanti: SettingsMode.EDIT })}>
                    Edit
                  </QButton>
                </>
              ) : (
                <QButton disabled variant="filled">
                  Save
                </QButton>
              )}
            </>
          )}
        </div>
      </QCardContent>
    </StyledQCard>
  );
};

const StyledQCard = styled(QCard)`
  width: 40%;
  min-width: 300px;
  margin: 20px;
  height: 100%;
`;

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;
